import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Inject,
  InjectionToken,
  NgZone,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { map, shareReplay, take } from 'rxjs/operators';
import { AnimationOptions } from 'ngx-lottie';

import { firstValueFrom, merge, Observable, of } from 'rxjs';

import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
  IS_SERVER_PLATFORM,
  PDFReportInformation,
  PrintColorsConstant,
  PrintOrderConstants,
  Referral,
  StockItem,
} from 'src/app/core';

import {
  PrintBlockContainerOption,
  PrintBlockInputInterface,
  CreateBlockOptions,
  getMainFileOrError,
  OrderTarefeOptions,
  PrintOrderInterface,
  PrintOrderRouteData,
  PrintBlockItem,
  SharedLink,
  TalcColor,
  Order,
  IntroService,
  OrderTypes,
  getMainFileOrNothing,
} from 'src/app/shared';

import {
  AnimationBuilderService,
  MainLogoAnimService,
  PrintBlockService,
  BindingsService,
  TypingsService,
  FilesService,
  OrderService,
  StockService,
  TitleService,
} from 'src/app/feature/services';

import { printOrderAnimations } from '../../animation';
import { SubSink } from 'subsink';
import { IntroJs } from 'intro.js';
import { BookAnimationDom } from 'src/app/shared/utilities/classes/BookAnimationDom';
import { ComponentBusService } from '../../services/component-bus.service';
import { DataSharingService } from 'src/app/shared/services/data-sharing.service';

import { config } from '../../../../../../config/config';

const _ = require('lodash');
export type TypingsType = { name: string; items: StockItem[] }[];

@Component({
  selector: 'print-order',
  templateUrl: './print-order.component.html',
  styleUrls: ['./print-order.component.scss'],
  animations: printOrderAnimations,
})
export class PrintOrderComponent implements OnInit, AfterViewInit {
  @ViewChild('autosize') autosize!: CdkTextareaAutosize;
  @ViewChild('printOrderContainer') printOrderContainer!: HTMLDivElement;
  @ViewChild('printAllCheckbox') printAllCheckboxEl: any;
  @ViewChild("container") private container: ElementRef;
  plasticCover: StockItem | undefined;
  bindSalaryFee: any;
  @HostListener('body.dragover', ['$event']) onDragover(event: any) {
    console.log(event);
  }

  printConfigs = config['blackAndWhitePrint'];

  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe(Breakpoints.Handset)
    .pipe(
      map((result) => result.matches),
      shareReplay()
    );

  triggerResize() {
    this.subsink.sink = this._ngZone.onStable
      .pipe(take(1))
      .subscribe(() => this.autosize.resizeToFitContent(true));
  }

  protected _title: string = 'مجموعه چاپ آنلاین، با کیفیت و ارزان';
  protected detailedLogging: boolean = false;
  protected subsink = new SubSink();
  protected mobileSubmitAnimationStyle = {
    width: '160px !important',
    position: 'absolute',
    top: '-12px',
    right: '30%' as any,
  };

  /**
   * The print blocks passed to mixed-range-select components.
   * they modify and add ranges directly into it.
   */
  public printBlocks: PrintBlockItem[] = [];
  public printBlockContainerOption!: PrintBlockContainerOption;

  public get colorfulType(): StockItem | undefined {
    if (!this.flatTypings) {
      return undefined;
    }

    const side = !!this.twoSided?.value ? 2 : 1;
    const color = 'c';

    return _.findLast(
      _.filter(this.flatTypings, { name: this.printSize?.value }),
      {
        name: this.printSize?.value,
        type: `${side}${color}`,
      }
    );
  }

  public get halfColorfulType(): StockItem | undefined {
    if (!this.flatTypings) {
      return undefined;
    }

    const side = !!this.twoSided?.value ? 2 : 1;
    const color = 'hc';

    return _.findLast(this.flatTypings, {
      name: this.printSize?.value,
      type: `${side}${color}`,
    });
  }

  public get blackWhiteType(): StockItem | undefined {
    if (!this.flatTypings) {
      return undefined;
    }

    const side = !!this.twoSided?.value ? 2 : 1;
    const color = 'bw';

    return _.findLast(this.flatTypings, {
      name: this.printSize?.value,
      type: `${side}${color}`,
    });
  }

  public get flatTypings(): StockItem[] | undefined {
    try {
      return this._typings
        .map((item) => {
          return item.items;
        })
        .reduce((a, b) => a.concat(b));
    } catch (e) {
      return undefined;
    }
  }

  /**
   * Intro.JS instance
   * we use this to give user a good guid about
   * how to use the form.
   */

  public introJs!: IntroJs;

  /**
   * The edit mode controller.
   */
  public editMode: boolean = false;
  public editModeEntity?: Order;

  /**
   * Submit animation options. The animation that
   * when you click on submit button gets played.
   */
  public submitAnimationOptions: AnimationOptions = {
    loop: false,
    autoplay: false,
    path: '../../../../assets/animation/speed-up.json',
  };

  /**
   * Main form controller.
   * When it's false, loading showed and form is
   * disabled.
   */
  public isReady = false;

  /**
   * Total price controller
   * that if you set this to something
   */
  public price!: string;

  /**
   * If order is shared get shared link and
   * set it there then we can use it for next operations.
   */
  public sharedLink!: SharedLink;

  /**
   * Referral Object
   * It's used to set referral object for order and then
   * after payment we can add points to users.
   */
  public referral!: Referral;

  /**
   * Talc colors that are load from router
   * resolver.
   */
  public talcColors: TalcColor[] = [];
  public filteredTalcColors: TalcColor[] = [];
  public selectedTalcColor?: TalcColor;

  /**
   * Default size that is selected at first
   * when the form is loaded and used to get
   * typings and setting the form select element.
   */
  public sizeSelectDefaultValue = 'A4';

  /**
   * The component indicator name.
   * Used to determine component name and
   * controlling the main landing banner display.
   */
  public indicator: string = 'print-order-component';

  /**
   * Tarefe panel controller and option
   * to control the toggle of panel and
   * other options.
   */
  public tarefeToggle: boolean = false;
  public tarefeOptions!: OrderTarefeOptions;

  /**
   * Status text is the text above the panel
   */
  public statusText!: string;

  /**
   * As you can see this is the size of every
   * input inside form blocks that assigned as %
   */
  public defaultInputBlockSize = 31.5;

  /**
   * Bindings that are added will be set
   * to below property.
   * Bindings & Typings are same StockItem
   * but they have difference in their structure.
   */
  public bindings: StockItem[] = [];
  public _typings: TypingsType = [];
  public set typings(typings: StockItem[]) {
    for (let index = 0; index < typings.length; index++) {
      const typingObject = typings[index];
      const indexOfTyping = _.findIndex(this._typings as any, [
        'name',
        typingObject.name,
      ]);
      if (typingObject.isBinding === false) {
        if (indexOfTyping < 0) {
          this._typings.push({ name: typingObject.name, items: [typingObject] });
        } else this._typings[indexOfTyping].items.push(typingObject);
      }
    }
  }

  /**
   * Single pages that are selected.
   * @deprecated
   */
  public _singlePages: number[] = [];
  public isRangesSelected: boolean = false;

  /**
   * Animation DOM controller
   * to animate animation svg.
   */
  public animationDomController?: BookAnimationDom;

  /**
   * Constant of print colors that are used
   * within typings and generating price.
   */
  public printColorsConst = PrintColorsConstant;

  /**
   * This property is the main property that determines
   * if file is uploaded or not.
   */
  public fileUploadReport?: PDFReportInformation;

  /**
   * Range selection overlays controllers and
   * properties.
   */
  public _halfOverlayOpened: boolean = false;
  public _fullOverlayOpened: boolean = false;

  public _halfRangeSingleItems: PrintBlockItem[] = [];
  public _fullRangeSingleItems: PrintBlockItem[] = [];
  public _generalRangeSingleItems: PrintBlockItem[] = [];

  /**
   * Controls the toggle of print all input and checkbox
   */
  public printAllToggleController: boolean = true;


  public customPageNoVlidation: boolean = true
  @HostListener('document:click', ['$event'])
  onClick(event: Event) {
    if (this.tarefeToggle) {
      this.tarefeToggle = false;
    }
  }

  constructor(
    protected router: Router,
    protected _ngZone: NgZone,
    protected fileService: FilesService,
    protected titleService: TitleService,
    protected orderService: OrderService,
    protected typingsService: TypingsService,
    protected bindingsService: BindingsService,
    protected printBlockService: PrintBlockService,
    protected breakpointObserver: BreakpointObserver,
    protected snackbar: MatSnackBar,
    protected mainLogoAnimation: MainLogoAnimService,
    protected activatedRoute: ActivatedRoute,
    protected animationBuilder: AnimationBuilderService,
    @Inject(IS_SERVER_PLATFORM) protected isServer: boolean,
    protected stockService: StockService,
    protected introJsService: IntroService,
    protected busService: ComponentBusService,
    protected dataSharingService: DataSharingService
  ) {
    if (!isServer) this.introJs = introJsService?.createInstance();

    const hostName = 'https://ziraxi.com';

    this.router.events.subscribe((event) => {
      let url = this.router.url;
      if (url.includes('black-and-white-print')) {
        this.printConfigs = config['blackAndWhitePrint'];
      } else if (url.includes('half-color-print')) {
        this.printConfigs = config['halfColorPrint'];
      } else if (url.includes('combined-printing')) {
        this.printConfigs = config['mixedPrint'];
      } else if (url.includes('full-color-print')) {
        this.printConfigs = config['colorfulPrint'];
      } else if (url.includes('online-print')) {
        this.printConfigs = config['onlinePrint'];
      } else if (url.includes('cheap-print')) {
        this.printConfigs = config['cheapPrint'];
      } else if (url.includes('booklet-print')) {
        this.printConfigs = config['bookletPrint'];
      } else if (url.includes('thesis-binding')) {
        this.printConfigs = config['ThesisOrder'];
      } else if (url.includes('thesis-editing')) {
        this.printConfigs = config['ThesisEditOrder'];
      } else {
        this.printConfigs = [];
      }
    });
  }

  /** Main form field. */
  public orderFormGroup = new FormGroup({
    pageNo: new FormControl(),
    printAll: new FormControl(),
    binding: new FormControl({ value: 0, disabled: true }),
    twoSided: new FormControl(true),
    talcColor: new FormControl(''),
    moreDetails: new FormControl(''),
    customPageNo: new FormControl(),
    range: new FormControl('', [
      Validators.max(this.fileUploadReport?.numpages || 1000),
    ]),
    copies: new FormControl('1', [Validators.required, Validators.min(1)]),
    printSize: new FormControl(
      PrintOrderConstants.DEFAULT_PRINT_SIZE,
      Validators.required
    ),
    caseType: new FormControl(
      PrintOrderConstants.DEFAULT_CASETYPE_VALUE,
      Validators.required
    ),
    pageColor: new FormControl(this.printColorsConst.BLACK_WHITE, [
      Validators.required,
    ]),
  });

  beforeUpload = true;

  get printSize() {
    return this.orderFormGroup.get('printSize');
  }

  get pageColor(): any {
    return this.orderFormGroup.get('pageColor');
  }

  get copies() {
    return this.orderFormGroup.get('copies');
  }

  get twoSided(): any {
    return this.orderFormGroup.get('twoSided');
  }

  get range() {
    return this.orderFormGroup.get('range');
  }

  public get printAll() {
    return this.orderFormGroup.get('printAll');
  }

  get caseType() {
    return this.orderFormGroup.get('caseType');
  }

  get binding() {
    return this.orderFormGroup.get('binding');
  }

  get talcColor() {
    return this.orderFormGroup.get('talcColor');
  }

  get pageNo() {
    return this.orderFormGroup.get('pageNo');
  }

  get customPageNo() {
    return this.orderFormGroup.get('customPageNo');
  }

  get moreDetails() {
    return this.orderFormGroup.get('moreDetails');
  }

  public getPrintType(): OrderTypes {
    return 'print';
  }

  disableForm() {
    this.customPageNo?.disable();
    this.printAll?.disable();
    this.copies?.disable();
    this.caseType?.disable();
    this.moreDetails?.disable();
    this.orderFormGroup.updateValueAndValidity();
    this.beforeUpload = true;
  }
  enableForm() {
    this.customPageNo?.enable();
    this.printAll?.enable();
    this.copies?.enable();
    this.caseType?.enable();
    this.moreDetails?.enable();
    this.orderFormGroup.updateValueAndValidity();
    this.beforeUpload = false;
  }

  filterTalcColors() {
    this.filteredTalcColors = [];
    this.talcColors.forEach((talc) => {
      if (this.filteredTalcColors.findIndex(t => talc.colorCode === t.colorCode) >= 0) { }
      else this.filteredTalcColors.push(talc);
    });
  }
  async ngOnInit(): Promise<void> {
    try {
      // Set the referral link
      this.subsink.sink = this.activatedRoute.data.subscribe({
        next: (data: any) => {
          if (data?.referral) {
            this.sharedLink = data.referral;
          }
        },
      });

      // Setup animation listeners.
      this.isHandset$.subscribe((result) => {
        if (!result) {
          this.setupAnimation();
        }
      });

      // Get and set talc colors
      this.subsink.sink = this.stockService
        .getTalcColors()
        .subscribe((talcColors) => {
          if (talcColors && talcColors.length >= 1)
            this.talcColors = talcColors;
          this.filterTalcColors();
        });
      await this.getAndSetOrderPreNeeds();

      // Set listeners for price calculation.
      await this.setListeners();

      this.subsink.sink = this.orderService.priceUpdate$.subscribe((price) => {
        this.price = price.toString();
      });

      await this.constructSetPrintBlockContainerOptions();

      await this.setRouterResolverData(
        await this.extractRouteDataPrintOrder(this.activatedRoute)
      );

      if (this.editMode) {
        this.router.routeReuseStrategy.shouldReuseRoute = () => false;
      }

      const params = await firstValueFrom(this.activatedRoute.params);
      const routeData = await firstValueFrom(this.activatedRoute.data);

      if (params['id'] && routeData['editMode']) {
        this.editMode = true;
        this.editModeEntity = await this.orderService.getOneOrder(
          +params['id']
        );

        await this.bindOrderToComponent(this.editModeEntity);
      }

      this.bindSalaryFee = await this.orderService.getBindSalaryFee('orderConf');
      console.log('bindSalaryFee',this.bindSalaryFee)

    } catch (e) {
      if (!!!this.isServer) {
        throw e;
      }

      return;
    }
    this.disableForm();
    if (this.fileService.uploadedFile) {
      this.saveFileUploadReport(this.fileService.uploadedFile);
      this.statusText = this.fileService.uploadedStatus;
      this.enableForm();
    }
  }

  ngAfterViewInit(): void {
    if (!this.isServer) {
      this.introJs.addSteps([
        {
          element: '.upload-main .dropzone',
          title: 'آپلود فایل',
          intro:
            'با انتخاب این گزینه، صفحه‌ای برای شما باز خواهد شد.<br/>فایل خود را به آن بکشید یا فایل مورد نظر را انتخاب کنید.',
        },
        {
          element: '#printSize .mdc-notched-outline',
          title: 'اندازه کاغذ',
          intro:
            'سایز کاغذ را انتخاب کنید.<br/>قیمت بر اساس اندازه کاغذ انتخاب شده، متغیر خواهد بود.',
        },
        {
          element: '#printColor .mdc-notched-outline',
          title: 'نوع چاپ',
          intro:
            'نوع چاپ مورد نظر را انتخاب کنید.<br/>در صورت انتخاب چاپ ترکیبی، صفحات نیمه رنگی و تمام رنگی مورد نظر را در باکس پایین انتخاب کنید.',
        },
        {
          element: '#printBtnToggle',
          title: 'یک رو / دو رو',
          intro:
            'در صورت انتخاب گزینه دو رو، هر دو صفحه از فایل شما بر روی یک برگ کاغذ به‌صورت پشت و رو چاپ خواهد شد.',
        },

        {
          element: '#pageRange .mdc-notched-outline',
          title: 'تعداد صفحات',
          intro:
            'تعداد صفحات مورد نظر از فایل انتخابی را مشخص کنید.<br/>مثال: اگر عدد 90 را وارد کنید، از صفحه 1 تا 90 فایل آپلود شده، پرینت خواهد شد.<br/>*برای انتخاب بازه‌ای از صفحات، آن را در قسمت «توضیحات» وارد کنید.',
        },

        {
          element: '#pageRange .print-all',
          title: 'همه صفحات',
          intro: 'با انتخاب این گزینه، کل صفحات فایل مورد نظر پرینت خواهد شد.',
        },

        {
          element: '#copiesNumber .mdc-notched-outline',
          title: 'تعداد نسخه',
          intro:
            'اگر می‌خواهید از یک فایل چند نسخه پرینت شود، تعداد نسخه مورد نظر را در این قسمت وارد کنید.',
        },

        {
          element: '#printCover .mdc-notched-outline',
          title: 'صحافی یا کاور',
          intro:
            'با انتخاب گزینه کاور، سفارش شما در کاور پلاستیکی قرار داده می‌شود.<br/>در صورت انتخاب گزینه صحافی، نوع صحافی مورد نظر را در کادر بعدی انتخاب کنید.',
        },

        {
          element: '#printBinding .mdc-notched-outline',
          title: 'نوع صحافی',
          intro:
            'نوع صحافی مورد نظر خود را انتخاب کنید.<br/>با انتخاب هر یک از گزینه‌های طلق دار، رنگ طلق دلخواهتان را در کادر بعدی برگزینید.',
        },

        {
          element: '#talcColorSelector',
          title: 'انتخاب رنگ طلق',
          intro: 'رنگ طلق یا جلد صحافی مورد نظر خود را انتخاب کنید',
        },

        {
          element: '#halfColorRangeSelect .item-list',
          title: 'صفحات نیمه رنگی',
          intro:
            'بازه صفحات نیمه‌ رنگی (مثال: 20 الی 10) را وارد کرده و سپس علامت + را انتخاب کنید.<br/>یا در قسمت «تک صفحه»، شماره هر صفحه را وارد کرده و علامت + را بزنید.<br/>می‌توانید چند بازه و یا چند تک‌برگ انتخاب کنید.',
        },

        {
          element: '#fullColorRangeSelect .item-list',
          title: 'صفحات تمام رنگی',
          intro:
            'بازه صفحات تمام رنگی (مثال: 35 الی 25) را وارد کرده و سپس علامت + را انتخاب کنید.<br/>یا در قسمت «تک صفحه»، شماره هر صفحه را وارد کرده و علامت + را بزنید.</br>می‌توانید چند بازه و یا چند تک‌برگ انتخاب کنید.',
        },

        {
          element: '#printOrderExtraInformation .mdc-notched-outline',
          title: 'توضیحات',
          intro:
            'هر توضیح دیگری دررابطه‌با سفارش خود دارید، در این قسمت وارد کنید.',
        },

        {
          element: '#totalPriceSection',
          title: 'قیمت',
          intro:
            'در این قسمت می‌توانید قیمت را به صورت زنده با تغییر گزینه‌ها ببینید',
        },

        {
          element: '#submitBtn',
          title: 'ثبت سفارش',
          intro:
            'با کلیک روی این قسمت، شما می‌توانید سفارش را ثبت کنید. بعد از ثبت سفارش، خرید شما در قسمت سبد خرید قابل مشاهده خواهد بود.',
        },

        {
          element: '#tarefeActionButton',
          title: 'تعرفه‌ها',
          intro:
            'با کلیک روی این دکمه، می‌توانید تعرفه‌های مختلف را مشاهده کنید.',
        },
      ]);

      this.introJs.setOptions({
        nextLabel: 'بعدی',
        prevLabel: 'قبلی',
        doneLabel: 'اتمام',
        scrollToElement: false,
      });
    }
  }

  toggleBinding() {
    if (this.caseType?.value === 'binding') this.binding?.enable();
    else {
      this.binding?.disable();
      this.binding?.setValue(0);
    }
    this.filterTalcColors();
  }

  public navigateTo(event: any) {
    const scrollTop = this.container.nativeElement?.scrollHeight;
    switch (event) {
      case 'hc': {
        // this.router.navigate(['/half-color-print']);
        this.router.navigate(
          ['/half-color-print'],
        ).finally(() => {
          setTimeout(() => {
            window.scrollTo(0, scrollTop)
          }, 1);
        });
        break;
      }

      case 'c': {
        // this.router.navigate(['/full-color-print']);
        this.router.navigate(
          ['/full-color-print'],
        ).finally(() => {
          setTimeout(() => {
            window.scrollTo(0, scrollTop)
          }, 1);
        });
        break;
      }
      case 'mixed': {
        // this.router.navigate(['/combined-printing']);
        this.router.navigate(
          ['/combined-printing'],
        ).finally(() => {
          setTimeout(() => {
            window.scrollTo(0, scrollTop)
          }, 1);
        });
        break;
      }
      case 'bw': {
        // this.router.navigate(['/black-and-white-print']);
        this.router.navigate(
          ['/black-and-white-print'],
        ).finally(() => {
          setTimeout(() => {
            window.scrollTo(0, scrollTop)
          }, 1);
        });
        break;
      }

      default:
        break;
    }
  }

  /**
   * Get price of selected constant
   * @param size Typing name, for example: A4
   * @param side The side of print.
   * @returns
   */
  public getTarefePrice(size: 'A4' | 'A5' | 'A3', side: 1 | 2) {
    try {
      return {
        c: this._typings
          .filter((item) => item.name === size)[0]
          .items.filter((stock) => stock.type === `${side}c`)[0].price,
        hc: this._typings
          .filter((item) => item.name === size)[0]
          .items.filter((stock) => stock.type === `${side}hc`)[0].price,
        bw: this._typings
          .filter((item) => item.name === size)[0]
          .items.filter((stock) => stock.type === `${side}bw`)[0].price,
      };
    } catch (e) {
      throw new Error(`${size} size not found`);
    }
  }

  protected recreateComponent() {
    setTimeout(() => {
      const currentRoute = this.activatedRoute.snapshot.url.join('/');
      this.router
        .navigateByUrl('/redirecting', { skipLocationChange: true })
        .then(() => {
          this.router.navigate([currentRoute]);
        });
    }, 0);
  }

  public checkPrintAll() {
    if (!this.printAll?.value) {
      this.printAll?.setValue(true);
    } else {
      this.printAll.setValue(false);
    }
  }

  protected async bindOrderToComponent(order: Order) {
    const mainFile = getMainFileOrNothing(order.files);

    if (!mainFile) {
      throw new Error('Can not edit this order');
    }

    this.orderFormGroup.setValue({
      binding: order.binding?.id || 0,
      copies: `${order.copiesNumber}` || `1`,
      moreDetails: order.moreDetails || '',
      pageColor: this.printColorsConst.BLACK_WHITE,
      pageNo: order.pageCount,
      printAll: false,
      caseType: order.binding ? 'binding' : 'plastic-cover',
      printSize: 'A4',
      customPageNo: 0,
      twoSided: mainFile?.fileBlocks?.every((item) => {
        return item.stock?.type?.startsWith('1');
      })
        ? false
        : true,
      range: '1',
      talcColor: 'red',
    });

    this.printBlocks = mainFile.fileBlocks!;
    this.selectedTalcColor = order.talcColor;
    this.fileUploadReport = await firstValueFrom(
      this.fileService.getSharedDocumentInformation(mainFile?.id)
    );

    this.updateOrderPrice();
  }

  /**
   * Get animation offset
   */
  public getAnimationOffset() {
    return 1100;
  }

  /**
   * When the file is uploaded you will get the result
   * and then you can use this method to save it.
   * @param fileUploadReport
   */
  public saveFileUploadReport(fileUploadReport: PDFReportInformation) {
    if (!fileUploadReport) {
      return;
    }

    // if (fileUploadReport.numpages > 5000) {
    //   this.snackbar.open(
    //     `تعداد صفحات آپلود شده نباید بیشتر از 5000 صفحه باشد.`,
    //     'باشه',
    //     { duration: 3000 }
    //   );
    //   return;
    // } else if (fileUploadReport.numpages < 10) {
    //   this.snackbar.open(
    //     `تعداد صفحات آپلود شده نباید کمتر از 10 صفحه باشد.`,
    //     'باشه',
    //     { duration: 3000 }
    //   );
    //   return;
    // }

    this.enableForm();
    this.fileUploadReport = fileUploadReport;
    this.pageNo?.setValue(fileUploadReport.numpages);
    this.updateOrderPrice();
  }

  /**
   * On status change we update our status realtime.
   */
  public onStatus(status: string) {
    this.statusText = status;
  }

  public getPriceofSelectedOnePage(): string | undefined {
    if (!this._typings || !!!this._typings?.length) {
      return '0';
    }

    const size = this.printSize?.value;
    const color = this.pageColor?.value;

    const sideNumber = this.twoSided?.value ? 2 : 1;

    if (color === 'mixed') {
      return;
    }

    return Array.from(this?._typings)
      .find((item) => item?.name === size)!
      .items.filter((item) => item?.type === `${sideNumber}${color}`)[0]?.price;
  }

  /**
   * Using this you can toggle print all checkbox
   */
  public rangeSelectToggle(event: any) {
    const checkedValue = event?.checked ? true : false;

    if (!checkedValue) {
      this.range?.enable();
    }

    if (checkedValue) {
      this.range?.disable();
    }
  }

  public onFormChange(controller: FormControl | AbstractControl) {
    const parent = this;

    this.constructSetPrintBlockContainerOptions();

    return function (changedValue: string | number | boolean) {
      const isValid = controller?.valid || false;

      if (changedValue !== '' && isValid) {
        parent.updateOrderPrice();
      }
    };
  }

  public async setListeners(): Promise<void> {
    // const items = ['pageColor', 'twoSided', 'printSize'];

    for (let key in this.orderFormGroup.value) {
      const controller = this.orderFormGroup.get(key) as AbstractControl;
      this.subsink.sink = this.orderFormGroup
        .get(key)
        ?.valueChanges.subscribe(this.onFormChange(controller));
    }
  }

  public needsTalc(): boolean {
    try {
      const selectedBindingId = this.binding?.value;

      if (this.caseType?.value && this.caseType.value === 'plastic-cover') {
        return false;
      }

      if (
        selectedBindingId?.toString() !== '' &&
        selectedBindingId !== 0 &&
        !this.binding?.disabled
      ) {
        const selectedBindingObject = _.find(this.bindings, {
          id: selectedBindingId,
        });
        return (selectedBindingObject as any).needsTalc;
      }

      return false;
    } catch (e) {
      return false;
    }
  }

  public setColor(event: any) {
    this.talcColor?.setValue(event);
  }

  public setCopies(number: string) {
    this.copies?.setValue(number);
    this.updateOrderPrice();
  }

  public setTwoSided(event: 'oneSide' | 'twoSide') {
    this.twoSided?.setValue(event === 'twoSide');

    if (!this.isServer) {
      if (event == 'twoSide') {
        this.animationDomController?.toTwoSidePrint();
      } else {
        this.animationDomController?.toOneSidePrint();
      }
    }
  }

  public toggleOverlay(name: 'full' | 'half') {
    if (name === 'full') {
      this._halfOverlayOpened = false;
      this._fullOverlayOpened = !this._fullOverlayOpened;
    }

    if (name === 'half') {
      this._fullOverlayOpened = false;
      this._halfOverlayOpened = !this._halfOverlayOpened;
    }
  }

  public closeOverlay(name: 'full' | 'half') {
    if (name === 'full') {
      this._fullOverlayOpened = false;
    }
    if (name === 'half') {
      this._halfOverlayOpened = false;
    }
  }

  public onNewPrintBlockData(data: PrintBlockItem[]) {
    this.printBlocks = data;
    this.updateOrderPrice();
  }

  public printBlockInstructionChange() {
    if (!this.editMode) this.printBlocks = [];
  }

  public onTalcColorChange(talcColor: TalcColor) {
    if (!this.isServer)
      this.animationDomController?.changeTalcColor(talcColor.colorCode);
  }

  public async submit() {
    try {
      let order = this.oldDataProviderToPrintOrderInterface();
      for (let block of order?.blocks!) {
        if (block.stock) {
          delete block.stock;
        }
      }

      if (!order) {
        console.error(order);
        throw new Error('[submit]: Order object error...');
      }

      switch (this.editMode) {
        case false: {
          await this.orderService.save({ type: 'print', order });
          this.resetComponent();
          break;
        }

        case true: {
          await this.orderService.updateRaw({ order });
          break;
        }

        default:
          break;
      }
    } catch (e) {
      this.snackbar.open('مشکلی در ثبت سفارش پیش آمد', '', { duration: 1000 });
    }
  }

  /**
   * When you pass the file through the main page.
   * @param {PDFReportInformation} pdf data.
   */
  public printOrderUsingLink(data: PDFReportInformation) {
    this.statusText = 'در حال محاسبه قیمت';
    this.saveFileUploadReport(data);
    this.updateOrderPrice();

    setTimeout(() => {
      (this.printOrderContainer as any).nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }, 100);
  }

  public updateOrderPrice() {
    if (this.detailedLogging) {
      console.log({
        ableToCalculatePrice: this.ableToCalculatePrice(),
        form: this.orderFormGroup.value,
      });
    }

    if (!this.ableToCalculatePrice()) {
      return;
    }

    const printOrderObject = this.oldDataProviderToPrintOrderInterface();
    const binding = this.getBinding();
    let rangeBindingPrice;
    if (binding?.name.indexOf('منگنه') == -1)
      rangeBindingPrice = this.getBindingSelectedPrice()
    if (!printOrderObject) throw new Error('Print order object not defined');
    if (this.caseType?.value == 'binding')
      this.orderService.calculatePrice(printOrderObject, 0, Number(this.bindSalaryFee.bindSalaryFee) ,binding, rangeBindingPrice);
    else{
      this.orderService.calculatePrice(printOrderObject, Number(this.plasticCover?.price), 0 , binding, rangeBindingPrice);
    }
  }

  public getImages(): number[] {
    return [];
  }

  public isNumber(value: any): boolean {
    return typeof value === 'number';
  }

  public startHelp() {
    if (!this.isServer) this.introJs.start();
  }

  // public recreateComponent() {
  //   // Destroy the component
  //   this.appRef.detachView(this.appRef.components[0].hostView);
  //   this.appRef.components[0].destroy();

  //   // Use setTimeout to create a new instance of the component after the current event loop has finished
  //   setTimeout(() => {
  //     const componentFactory =
  //       this.componentFactoryResolver.resolveComponentFactory(MyComponent);
  //     const componentRef =
  //       this.viewContainerRef.createComponent(componentFactory);
  //   }, 0);
  // }

  //-------------------------------------------------------Protected methods|

  protected setupAnimation() {
    if (!this.isServer) {
      const animationOffset = this.getAnimationOffset();
      this.mainLogoAnimation.updateHeight(animationOffset);
      this.animationDomController = new BookAnimationDom('#mainAnimation');

      this.subsink.sink = this.pageColor?.valueChanges.subscribe({
        next: (value: PrintColorsConstant) => {
          switch (value) {
            case PrintColorsConstant.BLACK_WHITE:
              this.animationDomController?.toBlackWhitePrint();
              break;

            case PrintColorsConstant.COLORFUL:
              this.animationDomController?.toColorfulPrint();
              break;

            case PrintColorsConstant.HALF_COLORFUL:
              this.animationDomController?.toHalfColorfulPrint();
              break;

            case PrintColorsConstant.MIXED:
              this.animationDomController?.toMixedPrint();
              break;

            default:
              break;
          }
        },
      });

      this.caseType?.valueChanges.subscribe({
        next: (value) => {
          if (value === 'plastic-cover') {
            this.animationDomController?.hideBinding();
          } else {
            const selectedColorCode =
              this.selectedTalcColor?.colorCode || '#fff';
            this.animationDomController?.changeTalcColor(selectedColorCode);
            this.animationDomController?.showBinding();
          }
        },
      });

      this.animationDomController!.hideBinding();
    }
  }

  protected setPrintBlocksFromOrder(printBlocks: PrintBlockItem[]) {
    this.printBlocks = printBlocks;
  }

  protected async setRouterResolverData(routerData: PrintOrderRouteData) {
    if (routerData?.editMode) {
      this.editMode = true;
    }

    if (routerData?.sharedFile) {
      this.printOrderUsingLink(routerData.sharedFile);
    }

    if (routerData?.order) {
      await this.setOrderInformation(routerData.order);
    }

    if (routerData?.pointer) {
      this.titleService.setRouterPointer(routerData?.pointer);

      switch (routerData.pointer) {
        case 'colorfulPrint':
          this.pageColor?.setValue('c');
          break;

        case 'blackAndWhitePrint':
          this.pageColor?.setValue('bw');
          break;

        case 'halfColorPrint':
          this.pageColor?.setValue('hc');
          break;
        case 'mixedPrint':
          this.pageColor?.setValue('mixed');
          break;

        default:
          break;
      }
    } else {
      this.titleService.setRouterPointer('');
    }
  }

  protected extractRouteDataPrintOrder(
    activatedRoute: ActivatedRoute
  ): Promise<PrintOrderRouteData> {
    return firstValueFrom(activatedRoute.data);
  }

  protected async setOrderInformation(order: Order) {
    try {
      const fileMetadata = await firstValueFrom(
        this.fileService.getSharedDocumentInformation(
          getMainFileOrError(order.files).id!
        )
      );

      const fileBlocks = getMainFileOrError(order.files).fileBlocks;
      this.sharedLink = order.sharedLink!;

      this.printOrderUsingLink(fileMetadata);
      this.pageColor?.setValue(order.color!);

      if (order?.talcColor && order?.binding) {
        this.selectedTalcColor = order.talcColor;
      }

      if (
        !(order?.color === PrintColorsConstant.MIXED) &&
        fileBlocks &&
        fileBlocks!?.length
      ) {
        this.printSize?.setValue(fileBlocks[0].stock?.name!);
        this.setTwoSided(
          fileBlocks[0]?.stock?.type?.startsWith('1') ? 'oneSide' : 'twoSide'
        );
      } else if (fileBlocks && order?.color === PrintColorsConstant.MIXED) {
        this.printSize?.setValue(fileBlocks[0].stock?.name!);
      }

      this.setPrintBlocksFromOrder(fileBlocks!);

      if (order?.binding) {
        this.caseType?.setValue('binding');
        this.binding?.setValue(order.binding.id);
      }

      this.copies?.setValue(order.copiesNumber.toString());

      this.updateOrderPrice();
    } catch (e) { }
  }

  protected ableToCalculatePrice() {
    if (this.detailedLogging) {
      console.log({
        oldDataProviderToPrintOrderInterface:
          this.oldDataProviderToPrintOrderInterface(),
        isReady: this.isReady,
      });
    }

    if (!this.oldDataProviderToPrintOrderInterface()) return false;
    // if (!this.fileUploadReport) return false;
    if (!this.isReady) return false;

    if(!this.fileService.uploadedFile) return false;
    return true;
  }

  protected oldDataProviderToPrintOrderInterface():
    | PrintOrderInterface
    | undefined {
    try {
      const info = this.moreDetails?.value || undefined;
      // hereeeee hami
      const fileId: number | undefined = +(
        this.fileUploadReport as PDFReportInformation
      )?.uploaded?.id!;

      const printBlock = this.getPrintBlocks();
      const data = this.gatherData();
      // const price = this.price;

      return {
        isMixed: this?.pageColor?.value === PrintColorsConstant.MIXED,
        referralLinkId: this?.sharedLink?.id,
        color: this.pageColor?.value,
        talcColorId: this.selectedTalcColor?.id,
        bindingId: data.binding?.id,
        copiesNumber: +data.copies,
        imageIds: this.getImages(),
        pageCount: data.pagesNo,
        blocks: printBlock,
        moreDetails: info,
        fileIds: [fileId],
        type: this.getPrintType(),
        // price,
      };
    } catch (e) {
      if (this.detailedLogging) {
        console.error(e);
      }
      return undefined;
    }
  }

  /**
   * Get every print blocks.
   * @returns {PrintBlockItem[]}
   */
  protected getPrintBlocks(): PrintBlockItem[] {
    const printAll = this.printBlockAllSelected();

    if (this.pageColor?.value === 'mixed') {
      return [...this.printBlocks, this.printBlockGenerateForWholeFile()];
    }

    if (printAll) {
      return [this.printBlockGenerateForWholeFile()];
    }

    if (this.range?.value) {
      return [this.printBlockGenerateByRange(this.range?.value)];
    }

    if (this.printBlocks.length === 0) {
      return [this.printBlockGenerateForWholeFile()];
    }

    return this.printBlocks;
  }

  /**
   * Include database in here to change.
   * @returns {boolean}
   */
  protected printBlockAllSelected(): boolean {
    if (this.printAll?.value) return true;
    if (!this.range?.value && this.printBlocks.length === 0) return true;

    return false;
  }

  protected get validPageNumber() {
    const filePageNumber = (this?.fileUploadReport as PDFReportInformation)
      ?.numpages;
    const customPageNo = this.orderFormGroup.get('customPageNo')?.value || 0;

    const validPageNumber =
      this.orderFormGroup.get('customPageNo')?.value ||
      (this?.fileUploadReport as PDFReportInformation)?.numpages ||
      this.pageNo?.value;

    if (filePageNumber && customPageNo >= filePageNumber) {
      return filePageNumber;
    }

    if (filePageNumber && customPageNo < 0) {
      return filePageNumber;
    }

    return validPageNumber;
  }

  protected printBlockGenerateForWholeFile(): PrintBlockItem {
    const range = `0-${this.validPageNumber}`;
    return this.printBlockGenerateByRange(range);
  }

  protected printBlockSingleRangeSelected(): PrintBlockItem | undefined {
    const range = this.range?.value;
    if (!range) return undefined;

    const options = this.printBlockGenerateCreateBlockOptions(range);
    const block = this.printBlockService.createBlock(options);

    return block;
  }

  protected printBlockGenerateByRange(range: string): PrintBlockItem {
    const options = this.printBlockGenerateCreateBlockOptions(range);

    return this.printBlockService.createBlock(options);
  }

  protected printBlockGenerateCreateBlockOptions(
    data: string
  ): CreateBlockOptions {
    const type = this.getPageType() as StockItem;

    return { type, data: data };
  }

  protected gatherData(): any {
    const type = this.getPageType();
    const binding = this.getBinding();

    let orderPriceSchema: any = {
      binding,
      pageType: type,
      orderType: 'print',
      range: this.range?.value,
      copies: this.copies?.value,
      pagesNo: this.pageNo?.value,
      printAll: this.printAll?.value,
      isTwoSided: this.twoSided?.value,
      talcColor: this.talcColor?.value,
      rangeSingleItems: this._generalRangeSingleItems,
      isMixed: this.pageColor?.value === 'mixed' ? true : false,
    };

    if (this.pageColor?.value === 'mixed') {
      const mixedTypes = this.getMixedTypes();
      orderPriceSchema = {
        ...mixedTypes,
        ...orderPriceSchema,

        fullItems: this._fullRangeSingleItems,
        halfItems: this._halfRangeSingleItems,
      };
    }

    return orderPriceSchema;
  }

  protected getBinding(): StockItem | undefined {
    return this.caseType?.value === 'binding'
      ? this.bindings.filter((bind) => bind.id === this.binding?.value).at(0)
      : undefined;
  }

  filteredBindings() {
    let sahafiList = this.bindings.filter((binding) => {
      return binding.type == this.printSize?.value || binding.name.indexOf('منگنه') != -1
    });
    return sahafiList
  }
  getBindingSelectedPrice() {
    let bindingSelectedPrice = 0;
    if (!this.orderFormGroup.value.binding) return
    if (this.filteredBindings().filter(binding => binding.id == this.orderFormGroup.value.binding)[0].name.indexOf('منگنه') != -1) {
      if (this.printAllToggleController) {
        const punchBindingPriceRangeBypageNo = Number(this.getBindingPriceRanges(this.bindings.filter(binding => binding.name == 'منگنه')[0], this.pageNo?.value));
        bindingSelectedPrice = punchBindingPriceRangeBypageNo;
      }
      else {
        const punchBindingPriceRangeByBycustomPageNo = Number(this.getBindingPriceRanges(this.bindings.filter(binding => binding.name == 'منگنه')[0], this.orderFormGroup.value.customPageNo));
        bindingSelectedPrice = punchBindingPriceRangeByBycustomPageNo;
      }
    }
    else {
      const talqBindingPrice = Number(this.filteredBindings().filter(binding => binding.id == this.orderFormGroup.value.binding)[0].price) * 2;
      if (this.printAllToggleController) {
        const springBindingPriceRangeBypageNo = Number(this.getBindingPriceRanges(this.bindings.filter(binding => binding.name == 'فنر')[0], this.pageNo?.value));
        bindingSelectedPrice = springBindingPriceRangeBypageNo + talqBindingPrice;
      }
      else {
        const springBindingPriceRangeByBycustomPageNo = Number(this.getBindingPriceRanges(this.bindings.filter(binding => binding.name == 'فنر')[0], this.orderFormGroup.value.customPageNo));
        bindingSelectedPrice = springBindingPriceRangeByBycustomPageNo + talqBindingPrice;
      }
    }
    return bindingSelectedPrice
  }


  getBindingPriceRanges(bindingSelected: StockItem, pageNoVal: number) {
    let priceBinding = 0;
    const blocks = this.oldDataProviderToPrintOrderInterface()?.blocks || [];
    if (blocks.length == 0) return;
    let pageNoValForCalc = pageNoVal;
    let oneSided = blocks[0].stock?.type?.indexOf("2") == -1 ? true : false;
    if (!oneSided) {
      let pageNumber = pageNoVal
      pageNoValForCalc = pageNumber / 2
      if (pageNumber % 2 != 0)
        pageNoValForCalc = Math.floor(pageNoValForCalc) + 1;

    }
    if (bindingSelected.priceRanges.length > 0) {
      bindingSelected.priceRanges.filter((priceRange) => {
        if (priceRange.min <= pageNoValForCalc && priceRange.max >= pageNoValForCalc) {
          priceBinding = priceRange.price
        }
      })
    }
    return priceBinding
  }

  protected getPageType() {
    if (this.pageColor?.value === 'mixed') {
      return this.blackWhiteType;
    }

    const printSize = this.printSize?.value || '';
    const typeObject = this._typings.find((o) => o.name === printSize);

    const typeName = this.orderService.generateColorTypeName(
      this.pageColor?.value,
      this.twoSided?.value
    );

    return (
      typeObject?.items.find((o) => o.type?.trim() == typeName.trim()) ||
      undefined
    );
  }

  protected async constructSetPrintBlockContainerOptions(): Promise<void> {
    const blocks = this.printBlockGenerateMixedInterfaces();
    this.printBlockContainerOption = { blocks };
  }

  protected printBlockGenerateMixedInterfaces(): PrintBlockInputInterface[] {
    this.printBlocks = [];
    this.updateOrderPrice();

    const interfaces: PrintBlockInputInterface[] = [];
    const type = this.getPageType();

    if (type && this.pageColor?.value === 'mixed') {
      const types = this.getMixedTypes();
      interfaces.push({ type: types.fullType, placeholder: 'تمام رنگی' });
      interfaces.push({ type: types.halfType, placeholder: 'نیمه رنگی' });
    } else {
      interfaces.push({ type: this.pageColor?.value, placeholder: '' });
    }

    return interfaces;
  }

  protected getMixedTypes(): Pick<any, 'fullType' | 'halfType'> {
    return { fullType: this.colorfulType, halfType: this.halfColorfulType };
  }

  /**
   * FIXME: Problem with resetting component.
   */
  protected resetComponent() {
    setTimeout(() => {
      this.busService.publish('order-add');
      this.busService.publish('order-reset');
      this.fileService.uploadedFile = undefined;
      this.fileService.uploadedStatus = '';
      this.recreateComponent();
    }, 1000);
  }

  protected async getAndSetOrderPreNeeds() {
    try {
      const stockItems = await firstValueFrom(this.setBindingsTypings());
      const typings: StockItem[] = [];
      console.log('stockItems:', stockItems)
      stockItems.forEach((stockItem) => {
        const searchForDuplicateBinding = this.bindings.findIndex((binding) => {
          return binding.id === stockItem.id || binding.name === stockItem.name;
        });

        // if (stockItem?.isBinding && searchForDuplicateBinding === -1) {
        //   this.bindings.push(stockItem);
        // }
        if (stockItem?.isBinding) {
          this.bindings.push(stockItem);
        }
        if (stockItem?.isType) {
          typings.push(stockItem);
        }
      });

      this.typings = typings;
      this.plasticCover = stockItems.find((item) => {
        return item.name == 'کاور'
      })

      /**
       * Tarefe options generate
       */

      this.tarefeOptions = {
        A4: {
          onesided: this.getTarefePrice('A4', 1),
          twosided: this.getTarefePrice('A4', 2),
        },
        A5: {
          onesided: this.getTarefePrice('A5', 1),
          twosided: this.getTarefePrice('A5', 2),
        },
        A3: {
          onesided: this.getTarefePrice('A3', 1),
          twosided: this.getTarefePrice('A3', 2),
        },
      };

      this.isReady = true;
      this.updateOrderPrice();
    } catch (e) {
      if (this.editMode) {
        this.snackbar.open('مشکلی در ویرایش به وجود آمد');
        this.router.navigate(['/']);
      }
      throw e;
    }
  }

  protected togglePrintAll() {
    this.printAllToggleController = !this.printAllToggleController;
  }

  protected setCustomPageEqPageNo() {
    const pageNoVal = this.pageNo?.value || '';
    const customPage = this.orderFormGroup.get('customPageNo');

    customPage?.setValue(pageNoVal);
  }

  public setBindingsTypings() {

    return merge(this.bindingsService.getAll(), this.typingsService.getAll());
  }

  protected printAllInputToggle() {
    this.customPageNoVlidation = true;
    if (this.beforeUpload) return;
    const pageNoVal = this.pageNo?.value;
    const customPageNoVal = this.orderFormGroup
      .get('customPageNo')
      ?.getRawValue();

    this.printAllToggleController = false;
    if (customPageNoVal > pageNoVal ||
      customPageNoVal < 10) {
      // this.snackbar.open(
      //   `تعداد صفحه وارد شده مجاز نمی باشد`,
      //   'باشه',
      //   { duration: 3000 }
      // );
      this.customPageNoVlidation = false;
      this.printAllCheckboxEl.writeValue(true);
      return;

    }
    if (
      !customPageNoVal ||
      !pageNoVal ||
      pageNoVal === customPageNoVal
    ) {
      this.printAllCheckboxEl.writeValue(true);
      return;
    }

    this.printAllCheckboxEl.writeValue(false);
  }

  protected printAllCheckboxToggle() {
    if (!this.beforeUpload) {
      this.printAllToggleController = true;
      this.printAllCheckboxEl.writeValue(true);
      this.setCustomPageEqPageNo();
    }
  }

  ngOnDestroy() {
    this.subsink.unsubscribe();
    const hostName = 'https://ziraxi.com';
    this.mainLogoAnimation.destroy();
  }
}
//for Build
